import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AdCard from "../Components/AdCard";
import { Container } from 'react-bootstrap';
// import { ThreeDot } from 'react-loading-indicators'; // Odkomentiraj, če uporabljaš
import CategoryFilter from "../Components/FilterComponents/CategoryFilter";
import PriceRangeFilter from '../Components/FilterComponents/PriceRangeFilter';
import ConditionFilter from '../Components/FilterComponents/ConditionFilter';
import LoadingScreen from '../LoadingScreen';
import "./AllUserAds.css";

const AllUserAds = () => {
    const { user_id } = useParams(); // ID uporabnika, čigar oglase gledamo
    const [sellerInfo, setSellerInfo] = useState(null); // Spremenil sem ime iz 'user' v 'sellerInfo' za boljšo jasnost
    const [items, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeListingsCount, setActiveListingsCount] = useState(0);
    const [priceLimits, setPriceLimits] = useState({ min: 0, max: 9999999 });
    const [filters, setFilters] = useState({
        category: '',
        subcategory: '',
        sort: 'newest',
        priceRange: [0, 9999999],
        itemCondition: []
    });
    const [availableCategories, setAvailableCategories] = useState([]);

    const debounce = (func, delay) => {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    };

    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem('viewMode') || 'list';
    });

    const toggleViewMode = () => {
        setViewMode(prev => {
            const newMode = prev === 'list' ? 'grid' : 'list';
            localStorage.setItem('viewMode', newMode);
            return newMode;
        });
    };

    useEffect(() => {
        const fetchPriceRange = async () => {
            try {
                const categoryId = filters.subcategory || filters.category;
                const params = new URLSearchParams();
                if (categoryId) params.append('categoryId', categoryId);
                if (user_id) params.append('userId', user_id);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/listings/pricerange?${params.toString()}`);
                if (!response.ok) throw new Error('Failed to fetch price range');
                const data = await response.json();
                const min = Number(data.minPrice) || 0;
                const max = Number(data.maxPrice) || 9999999;
                setPriceLimits({ min, max });
                setFilters(prev => ({
                    ...prev,
                    priceRange: [Math.round(min), Math.round(max)]
                }));
            } catch (error) {
                console.error('Error fetching price range:', error);
            }
        };
        fetchPriceRange();
    }, [filters.category, filters.subcategory, user_id]);

    useEffect(() => {
        const fetchUserAds = async () => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();
                if (filters.subcategory) queryParams.append('categoryId', filters.subcategory);
                else if (filters.category) queryParams.append('categoryId', filters.category);
                if (filters.priceRange) {
                    queryParams.append('minPrice', filters.priceRange[0]);
                    queryParams.append('maxPrice', filters.priceRange[1]);
                }
                if (filters.itemCondition && filters.itemCondition.length > 0) {
                    queryParams.append('itemCondition', filters.itemCondition.join(','));
                }
                if (filters.sort) queryParams.append('sort', filters.sort);

                // Klic API-ja ostane enak, saj user_id v poti specificira prodajalca
                const response = await fetch(`${process.env.REACT_APP_API_URL}/user/${user_id}/listings?${queryParams.toString()}`);
                const data = await response.json();

                // `data.listings` je array, `data.activeListingsCount` je številka
                const listingsData = Array.isArray(data.listings) ? data.listings : [];
                setAds(listingsData);
                
                if (data.activeListingsCount !== undefined) {
                    setActiveListingsCount(data.activeListingsCount);
                }

                // Nastavi informacije o prodajalcu iz prvega oglasa (če obstajajo oglasi in imajo sellerja)
                // To je za prikaz imena prodajalca v bannerju itd.
                if (listingsData.length > 0 && listingsData[0].seller) {
                    setSellerInfo(listingsData[0].seller);
                } else {
                    // Če ni oglasov ali seller podatkov, lahko poskusiš dobiti podatke o uporabniku posebej
                    // ali pa pustiš sellerInfo kot null / prikažeš generično sporočilo
                    // Za zdaj predpostavljamo, da želimo info iz prvega oglasa
                    setSellerInfo(null); // ali fetch User by user_id
                }

                const categoryIds = listingsData.flatMap(item => {
                    const category = item.category;
                    if (!category) return [];
                    const ids = [category.id];
                    if (category.parent_id) ids.push(category.parent_id);
                    return ids;
                });
                const uniqueCategories = [...new Set(categoryIds)].filter(Boolean);
                setAvailableCategories(uniqueCategories);

            } catch (error) {
                console.error("Error fetching items:", error);
            } finally {
                setLoading(false);
            }
        };

        // Debounce samo za priceRange, ostalo takoj
        const isPriceRangeChange = (prevFilters) => {
            if (!prevFilters) return true; // Prvi klic
            return prevFilters.priceRange[0] !== filters.priceRange[0] || prevFilters.priceRange[1] !== filters.priceRange[1];
        };

        // Shranimo prejšnje filtre za primerjavo, če je potrebno
        // Za zdaj bomo pustili poenostavljeno logiko debounce-a
        if (filters.priceRange) { // Predpostavka: debounce želimo samo, ko se priceRange spreminja
            const debouncedFetch = debounce(fetchUserAds, 500);
            debouncedFetch();
        } else {
            fetchUserAds();
        }

    }, [user_id, filters]); // Odvisnost od filters bo sprožila fetch ob vsaki spremembi filtra

    const formatDate = (dateString) => {
        if (!dateString) return "Neznan datum";
        const date = new Date(dateString);
        return date.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    return (
        <div className="bg-all-user-items">
            <div className="banner_user">
                <div className="overlay position-absolute"></div>
                <h1 className="banner-text-user">
                    {/* Uporabi sellerInfo za prikaz imena prodajalca */}
                    {sellerInfo ? `Oglasi uporabnika: ${sellerInfo.username}` : "Oglasi uporabnika"}
                </h1>
                {/* Uporabi sellerInfo za prikaz datuma pridružitve */}
                {sellerInfo && (
                    <>
                        <p className="user-joined">
                            <i className="fa fa-calendar-alt"></i> Pridružen: {formatDate(sellerInfo.created_at)}
                        </p>
                        <p className="active-listings">
                            <i className="fa fa-bullhorn"></i> Aktivnih oglasov: {activeListingsCount}
                        </p>
                    </>
                )}
            </div>

            <Container className="all-user-items">
                <button
                    className="filter-toggle d-md-none"
                    onClick={() => setMobileFiltersOpen(prev => !prev)}
                >
                    {mobileFiltersOpen ? (
                        <><i className="fa-solid fa-xmark" /> Skrij filtre</>
                    ) : (
                        <><i className="fa-solid fa-filter" /> Pokaži filtre</>
                    )}
                </button>
                <div className={`filters ${mobileFiltersOpen ? 'open' : ''}`}>
                    <div className="filter-section">
                        <CategoryFilter filters={filters} setFilters={setFilters} availableCategories={availableCategories} />
                    </div>
                    <div className="filter-section">
                        <PriceRangeFilter
                            filters={filters}
                            setFilters={setFilters}
                            minPrice={priceLimits.min}
                            maxPrice={priceLimits.max}
                        />
                    </div>
                    <div className="filter-section">
                        <ConditionFilter filters={filters} setFilters={setFilters} />
                    </div>
                </div>

                {loading ? (
                    <LoadingScreen />
                ) : items.length > 0 ? (
                    <div className="items-content">
                        <div className="sort_items">
                            <select value={filters.sort} onChange={(e) => setFilters(prev => ({ ...prev, sort: e.target.value }))}>
                                <option value="newest">Najnovejši</option>
                                <option value="oldest">Najstarejši</option>
                                <option value="price_asc">Cena: naraščajoče</option>
                                <option value="price_desc">Cena: padajoče</option>
                            </select>
                            <button className="view-toggle" onClick={toggleViewMode}>
                                {viewMode === 'list' ? <i className="fa-solid fa-th-large" /> : <i className="fa-solid fa-list" />}
                            </button>
                        </div>
                        <div className={`items ${viewMode}-view`}>
                            {items.map((item, index) => {
                                let image = null;
                                if (item.images && item.images.length > 0) {
                                    const primaryImage = item.images.find(img => img.is_primary === true);
                                    image = primaryImage ? primaryImage.image_url : item.images[0].image_url;
                                }
                                // Ime lokacije dobimo iz item.seller.location.city
                                // Lahko združiš city in country, če želiš.
                                const locationName = item.seller?.location?.city || "Neznana lokacija";

                                return (
                                    <div
                                        key={item.id}
                                        className="item-card-wrapper fade-in-up"
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        <AdCard
                                            id={item.id}
                                            images={item.images || []}
                                            category={item.category?.name || "Neznana kategorija"}
                                            title={item.title}
                                            location={item.seller?.location}
                                            price={item.price || "Cena po dogovoru"}
                                            created_at={item.created_at}
                                            viewMode={viewMode}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <p className="text-center">{sellerInfo ? `Uporabnik ${sellerInfo.username} trenutno nima aktivnih oglasov.` : 'Uporabnik trenutno nima aktivnih oglasov.'}</p>
                )}
            </Container>
        </div>
    );
};

export default AllUserAds;